import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalTextEntity } from "@lexical/react/useLexicalTextEntity";
import { $dfs, $findMatchingParent, mergeRegister } from "@lexical/utils";
import ModalBox from "components/Modal";
import type { UserVariables } from "components/shared/AgreementModal";
import { VARIABLE_REGEX } from "components/shared/AgreementModal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import type { IChannel } from "containers/Channels/types";
import { H3 } from "helpers/TypographyComponents";
import { isContractValidationPage, removeBrackets } from "helpers/utils";
import type { LexicalCommand, ParagraphNode, TextNode } from "lexical";
import {
    $createParagraphNode,
    $createTextNode,
    $getNodeByKey,
    $getRoot,
    $getSelection,
    $isParagraphNode,
    $isRangeSelection,
    $setSelection,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DELETE_CHARACTER_COMMAND,
    LexicalNode,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgreementTemplate } from "store/agreementsStore";
import type { ChannelCompensationGroup } from "store/channelStore";
import { useAppSelector } from "store/store";
import { v4 as uuidv4 } from "uuid";

import { recalculateNumberingArticles } from "./ArticlePlugin";
import { $isArticleContainerNode, ArticleContainerNode } from "./ArticlePlugin/ArticleContainerNode";
import type { ArticleTitleNode } from "./ArticlePlugin/ArticleTitleNode";
import { channelVariableLabel, parseChannelVariable } from "./channelVariable";
import type { FilledVariableNode } from "./LexicalFilledVariableNode";
import { $createFilledVariableNode, $isFilledVariableNode } from "./LexicalFilledVariableNode";
import { $createVariableNode, $isVariableNode, VariableNode } from "./LexicalVariableNode";
import type { ReferenceNode } from "./ReferenceNode";
import { $createReferenceNode, $isReferenceNode, MARK_AS_REFERENCE_COMMAND } from "./ReferenceNode";
import type { RepeatableContentNode } from "./RepeatablePlugin/RepeatableContentNode";
import { $isRepeatableContentNode } from "./RepeatablePlugin/RepeatableContentNode";
import { isArticleRepeatStorageKey, resolveRepeatableVariableKey } from "./RepeatablePlugin/repeatBy";
import { getArticleReference } from "./utils";
import type { AgreementVariables } from "./variables";

type ArticleNode = { depth: number; node: ArticleContainerNode };
type ArticleNodeWithExtra = {
    depth: number;
    title: string;
    number: string;
    key: string;
    ArticleNode: ArticleContainerNode;
    /**
     * formatted reference name
     */
    referenceName: string;
};

// Lexical React plugins are React components, which makes them
// highly composable. Furthermore, you can lazy load plugins if
// desired, so you don't pay the cost for plugins until you
// actually use them.
export const AutoFocusPlugin = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!editor.isEditable()) editor.focus();
        editor.update(() => recalculateNumberingArticles(true, true));
    }, [editor]);

    return null;
};

// When the editor changes, you can get notified via the
// OnChangePlugin!
/**
 * Not used, but it's a good example to create a plugin
 * @param param0.onChange - Callback to be called when the editor changes.
 */
interface IOnChangePlugin {
    onChange: (savedEditorData) => void;
    handleDirtyEditor: () => void;
}
export const OnChangePlugin = ({ onChange, handleDirtyEditor }: IOnChangePlugin) => {
    const [previousTextContent, setPreviousTextContent] = useState(""); // detect text changes
    const isFirstUpdate = useRef(true);

    // Access the editor through the LexicalComposerContext
    const [editor] = useLexicalComposerContext();
    // Wrap our listener in useEffect to handle the teardown and avoid stale references.
    useEffect(() => {
        // most listeners return a teardown function that can be called to clean them up.
        return editor.registerUpdateListener(({ editorState }) => {
            // Defer to avoid infinite loop: nested editor.update() triggers updateListener again
            const jsonString = JSON.stringify(editorState);
            queueMicrotask(() => {
                try {
                    editor.update(() => {
                        const htmlString = $generateHtmlFromNodes(editor, null);
                        onChange({ stringifiedBlocks: jsonString, html: htmlString });
                    });
                } catch (e) {
                    console.error("OnChangePlugin: Error generating HTML", e);
                }
            });
            editorState.read(() => {
                const currentTextContent = $getRoot().getTextContent();
                // check if it's first update
                if (isFirstUpdate.current) {
                    isFirstUpdate.current = false;
                    setPreviousTextContent(currentTextContent);
                    return;
                }
                if (currentTextContent !== previousTextContent) {
                    if (currentTextContent.trim().length > 0) {
                        handleDirtyEditor();
                    }
                    setPreviousTextContent(currentTextContent); // Mettre à jour le texte précédent
                }
            });
        });
    }, [editor, onChange]);
    return null;
};
/**
 * Lexical plugin to handle saving the editor state.
 * Will save the editor state in JSON format and HTML format.
 * @param param0.onEditorSave - Callback to be called when the editor is saved.
 */
export const EditorSavePlugin = ({ onEditorSave }: any) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            // Defer to avoid infinite loop: nested editor.update() triggers updateListener again
            const jsonString = JSON.stringify(editorState);
            queueMicrotask(() => {
                try {
                    editor.update(() => {
                        const htmlString = $generateHtmlFromNodes(editor, null);
                        onEditorSave({ stringifiedBlocks: jsonString, html: htmlString });
                    });
                } catch (e) {
                    console.error("EditorSavePlugin: Error generating HTML", e);
                }
            });
        });
    }, [editor, onEditorSave]);

    return null;
};

export const INSERT_TEXT_COMMAND: LexicalCommand<string> = createCommand("INSERT_TEXT_COMMAND");

export const InsertTextPlugin = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand<string>(
            INSERT_TEXT_COMMAND,
            (payload) => {
                editor.update(() => {
                    const selection = $getSelection();
                    if (selection) {
                        selection.insertText(payload);
                    }
                });
                return true;
            },
            COMMAND_PRIORITY_EDITOR,
        );
    }, [editor]);

    return null;
};

const REGEX = new RegExp(VARIABLE_REGEX, "mi");

interface IVariablePlugin {
    /**
     * All variables object
     */
    allVariables: AgreementVariables;
}
export const VariablePlugin = ({ allVariables }: IVariablePlugin) => {
    const [editor] = useLexicalComposerContext();
    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const formatTypes = useAppSelector((state) => state.titleFormatTypes.items);

    useEffect(() => {
        if (!editor.hasNodes([VariableNode])) {
            throw new Error("VariablePlugin: VariableNode not registered on editor");
        }
    }, [editor]);

    const createVariableNode = useCallback(
        (textNode: TextNode): VariableNode => {
            const variable = textNode.getTextContent(); // {{variable}}

            let variableLabel = allVariables[variable];
            const parsedChannel = parseChannelVariable(variable);

            if (parsedChannel?.kind === "channelGroup") {
                const group = channelCompensationGroups.find(
                    (group: ChannelCompensationGroup) => group.id === parsedChannel.id,
                );
                const formatName = formatTypes.find((formatType) => formatType.id === parsedChannel.formatTypeId)?.name;
                variableLabel = group?.name ? channelVariableLabel(group.name, formatName) : "";
            }

            if (parsedChannel?.kind === "channel") {
                const channel = channels.find((channel: IChannel) => channel.id === parsedChannel.id);
                const formatName = formatTypes.find((formatType) => formatType.id === parsedChannel.formatTypeId)?.name;
                variableLabel = channel?.name ? channelVariableLabel(channel.name, formatName) : "";
            }

            if (!variableLabel) return $createVariableNode(variable, ""); // personalized variable with brackets {{myVariable}}

            return $createVariableNode(variable, variableLabel);
        },
        [allVariables, channelCompensationGroups, channels, formatTypes],
    );

    const getVariableMatch = useCallback((text: string) => {
        const matchArr = REGEX.exec(text);
        if (matchArr === null) return null;

        const variableLength = matchArr[0].length;
        const startOffset = matchArr.index;
        const endOffset = startOffset + variableLength;
        const offsets = {
            end: endOffset,
            start: startOffset,
        };
        return offsets;
    }, []);

    useLexicalTextEntity<VariableNode>(getVariableMatch, VariableNode, createVariableNode);

    return null;
};

export const FillVariablesPlugin = ({
    userVariables,
    agreementUserVariables,
    hasMultiSignature,
}: {
    // user variables state
    userVariables: UserVariables;
    // agreement user variables
    agreementUserVariables: Record<string, any> | undefined;
    // If agreement uses multi signature
    hasMultiSignature: boolean;
}): JSX.Element | null => {
    const [editor] = useLexicalComposerContext();
    const [t] = useTranslation();

    useEffect(() => {
        if (!editor.hasNodes([VariableNode])) {
            throw new Error("FillVariablesPlugin: VariableNode not registered on editor");
        }
        /**
         * We can't only copy variables, we need to copy and differenciate them by adding a specific html attribute for each beneficiary.
         * Wanna call it : <span multi-beneficiary-variable-key={`beneficiaries.${index}.exemple`} />
         * It should look like :
         * 1st beneficiary : beneficiaries.0.name
         * 2nd one : beneficiaries.1.name
         * */

        return editor.registerNodeTransform(VariableNode, (varNode: VariableNode | FilledVariableNode) => {
            const variableText = removeBrackets(varNode.getTextContent());
            const isRepeatable = !!$findMatchingParent(varNode, $isRepeatableContentNode); // variable isn't in a repeatable block

            if (isContractValidationPage()) {
                const allNodes = $dfs();
                const repeatableBlocks = allNodes.filter((node) => $isRepeatableContentNode(node.node)) as Readonly<{
                    depth: number;
                    node: RepeatableContentNode;
                }>[];
                repeatableBlocks.forEach((block) => {
                    const paragraphs = block.node.getChildren();
                    paragraphs.forEach((paragraph) => {
                        (paragraph as ParagraphNode).getChildren().forEach((node) => {
                            if ($isVariableNode(node) && isRepeatable) {
                                const variableName = removeBrackets(node.__text);
                                const variableKey = resolveRepeatableVariableKey({
                                    repeatBy: block.node.getRepeatBy(),
                                    index:
                                        block.node.getRepeatBy() === "article"
                                            ? block.node.getArticleIndex()
                                            : block.node.getBeneficiaryIndex(),
                                    variableName,
                                    articleValues: userVariables,
                                });
                                const displayValue = userVariables[variableKey ?? variableName] ?? " ";
                                node.replace(
                                    $createFilledVariableNode(
                                        displayValue,
                                        displayValue,
                                        node.getTextContent(),
                                        variableKey ?? "",
                                        node.getFormat(),
                                    ),
                                );
                            }
                        });
                    });
                });
            }

            if ($isVariableNode(varNode) && !isRepeatable)
                varNode.replace(
                    $createFilledVariableNode(
                        userVariables[variableText] ?? " ",
                        userVariables[variableText] ?? " ",
                        varNode.getTextContent(),
                        "",
                        varNode.getFormat(),
                    ),
                );
        });
    }, [editor]);

    useEffect(() => {
        editor.update(() => {
            $setSelection(null);
            const allNodes = $dfs();
            const allVariableNodes = allNodes.filter((node) => $isFilledVariableNode(node.node)) as unknown as {
                depth: number;
                node: FilledVariableNode;
            }[];
            const agreementVariables = Object.keys(agreementUserVariables ?? {});
            allVariableNodes.forEach((node) => {
                const scopedKey = node.node.multiBeneficiaryKey ?? "";
                const multiBeneficiaryKey =
                    hasMultiSignature && agreementVariables.find((variable) => variable === scopedKey);
                const variableName = removeBrackets(node.node.variable);
                let newVariableName = "";
                if (isArticleRepeatStorageKey(scopedKey)) {
                    newVariableName = userVariables[scopedKey] ?? " ";
                } else if (multiBeneficiaryKey) {
                    newVariableName = userVariables[multiBeneficiaryKey] ?? " ";
                } else {
                    newVariableName = userVariables[variableName] ?? " ";
                }
                node.node.setTextContent(newVariableName);
                node.node.setVariableName(newVariableName);
            });
        });
    }, [userVariables]);
    return null;
};

export const ReferencePlugin = (): JSX.Element | null => {
    const [editor] = useLexicalComposerContext();
    const [t] = useTranslation();

    const [showChooseArticleModal, setShowChooseArticleModal] = useState(false);
    const [articleNodeList, setArticleNodeList] = useState<ArticleNodeWithExtra[]>([]);
    const [chosenArticle, setChosenArticle] = useState<ArticleNodeWithExtra>();

    const onArticleChosen = (value: ArticleNodeWithExtra) => {
        if (!value) return;
        setChosenArticle(value);
        setShowChooseArticleModal(false);

        editor.update(() => {
            const selection = $getSelection();
            if (selection) {
                const articleReference = value?.referenceName;
                const refKey = uuidv4();
                const currentReferenceKey = value.ArticleNode.getReferenceKey();
                let referenceNode;

                if (currentReferenceKey) {
                    referenceNode = $createReferenceNode(articleReference, currentReferenceKey);
                } else {
                    value.ArticleNode.setReferenceKey(refKey);
                    referenceNode = $createReferenceNode(articleReference, refKey);
                }
                selection.insertNodes([referenceNode]);
                // After create a Reference, insert empty string " ", to be able to click after to add text
                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const container = $findMatchingParent(selection.anchor.getNode(), $isParagraphNode);
                    const text = $createTextNode(" ");
                    container?.getLastChild()?.insertAfter(text);
                }
            }
        });
        setChosenArticle(undefined);
    };

    const renderArticleOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list">
            <span style={{ fontSize: "14px", paddingLeft: option.depth * 6 }}>{option.title}</span>
        </li>
    );

    useEffect(() => {
        if (!editor.hasNodes([VariableNode])) {
            throw new Error("ReferencePlugin: ReferenceNode not registered on editor");
        }

        return mergeRegister(
            editor.registerCommand<string>(
                MARK_AS_REFERENCE_COMMAND,
                () => {
                    editor.update(() => {
                        setShowChooseArticleModal(true);
                        const allNodes = $dfs();
                        const allArticleNodes: ArticleNode[] = allNodes.filter((node) =>
                            $isArticleContainerNode(node.node),
                        ) as unknown as ArticleNode[];
                        setArticleNodeList(
                            allArticleNodes.map((node) => {
                                const articleTitle = node.node.getFirstChild<ArticleTitleNode>()?.getTextContent();
                                const articleReference = getArticleReference(node.node);
                                // match pattern exmeples : "Article 1." or "1.2.3"
                                const numbersMatch =
                                    articleTitle?.match(/(Article \d+(\.\d+)*\.)/) ||
                                    articleTitle?.match(/^(\d+(\.\d+)*\.)/);
                                let articleNumber;
                                if (numbersMatch) articleNumber = numbersMatch[0];

                                return {
                                    depth: node.depth,
                                    title: node.node.getFirstChild<ArticleTitleNode>()?.getTextContent() || "",
                                    number: articleNumber,
                                    key: node.node.getKey(),
                                    ArticleNode: node.node,
                                    referenceName: articleReference,
                                };
                            }),
                        );
                    });
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            editor.registerMutationListener(ArticleContainerNode, (mutatedNodes, payload) => {
                for (const [nodeKey, mutation] of mutatedNodes) {
                    if (mutation == "destroyed") {
                        editor.update(() => {
                            let articleNode: ArticleContainerNode | null = null;
                            let articleReferenceKey: string | undefined;

                            // get all reference nodes that reference this article
                            const allReferenceNodes = $dfs().filter((node) =>
                                $isReferenceNode(node.node),
                            ) as unknown as { depth: number; node: ReferenceNode }[];

                            // load the previous state to get the article node before it was deleted
                            payload.prevEditorState.read(() => {
                                articleNode = $getNodeByKey(nodeKey, payload.prevEditorState);
                                articleReferenceKey = articleNode?.getReferenceKey();
                            });

                            // check which node references the deleted article
                            const referenceNodes = allReferenceNodes.filter(
                                (node) => node.node.getArticleReferenceKey() === articleReferenceKey,
                            );
                            // set error mode and error text for each reference node whose referenced article were deleted
                            if (referenceNodes.length > 0) {
                                referenceNodes.forEach((node) => {
                                    node.node.setError(true);
                                    node.node.setTextContent(
                                        node.node.getTextContent() + " (" + t("RichEditor.ArticleDeleted") + ")",
                                    );
                                });
                            }
                        });
                    }

                    // if (mutation == "created") {
                    //      Add code here to handle changes on references when an Article is added
                    // }
                }
            }),
        );
    }, [editor]);

    return (
        <ModalBox
            mainTitle={t("RichEditor.ChooseArticle")}
            open={showChooseArticleModal}
            onClose={() => setShowChooseArticleModal(false)}
        >
            <>
                <H3>{t("RichEditor.PleaseChooseTheArticle")}</H3>
                <AutocompleteFormComponent
                    value={chosenArticle}
                    onChange={(e, value) => onArticleChosen(value)}
                    options={articleNodeList}
                    renderOptionText={(option) => option.title || "nope"}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderOption={renderArticleOption}
                    defaultFilterOptions
                />
            </>
        </ModalBox>
    );
};

export const SetDraftDataPlugin = ({ agreement }: { agreement: AgreementTemplate }): JSX.Element | null => {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        editor.update(() => {
            // In the browser you can use the native DOMParser API to parse the HTML string.
            const parser = new DOMParser();
            const dom = parser.parseFromString(agreement.html, "text/html");

            // Once you have the DOM instance it's easy to generate LexicalNodes.
            const nodes = $generateNodesFromDOM(editor, dom);

            // Select the root
            const root = $getRoot();

            const paragraphNode = $createParagraphNode();

            nodes.forEach((n) => paragraphNode.append(n));

            root.append(paragraphNode);
        });
    }, [agreement]);
    return null;
};
